/**
 * Demo Mode Data - The Carter Family
 * 
 * A complete, realistic fictional family for demonstrating Forget Me Not:
 * - Michael Carter (Father, 45) - Software Engineer at TechCorp
 * - Emily Carter (Mother, 43) - Librarian at Oakville Public Library
 * - Emma Carter (Daughter, 14) - High school freshman
 * - Jake Carter (Son, 11) - 5th grader, loves sports
 * - Buddy (Golden Retriever, 5) - Family dog
 * 
 * All data is interconnected to show how a real family would use this app
 * after years of building their Emergency Binder.
 */

// Helper function to get a date X days from now
const getDateDaysFromNow = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

// Helper function to get a past date
const getPastDate = (daysAgo) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split('T')[0];
};

// Generate unique IDs for demo data
const generateId = (prefix) => `demo_${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ============= THE CARTER FAMILY USER =============

export const DEMO_USER = {
  id: "demo_user_carter",
  email: "michael.carter@demo.forgetmenot.app",
  name: "Michael Carter",
  picture: null,
  email_verified: true,
  subscription_status: "premium",
  is_demo: true,
  created_at: "2023-01-15T10:00:00Z"
};

// ============= FAMILY PROFILES =============

export const DEMO_PROFILES = [
  {
    id: "demo_profile_michael",
    user_id: "demo_user_carter",
    name: "Michael Carter",
    relationship: "self",
    type: "adult",
    date_of_birth: "1979-06-15",
    blood_type: "O+",
    height: "5'11\"",
    weight: "195 lbs",
    primary_language: "English",
    organ_donor: true,
    is_primary: true,
    ssn_last_four: "4521"
  },
  {
    id: "demo_profile_emily",
    user_id: "demo_user_carter",
    name: "Emily Carter",
    relationship: "spouse",
    type: "adult",
    date_of_birth: "1981-09-22",
    blood_type: "A+",
    height: "5'6\"",
    weight: "145 lbs",
    primary_language: "English",
    organ_donor: true,
    ssn_last_four: "7832"
  },
  {
    id: "demo_profile_emma",
    user_id: "demo_user_carter",
    name: "Emma Carter",
    relationship: "daughter",
    type: "child",
    date_of_birth: "2011-03-08",
    blood_type: "A+",
    height: "5'4\"",
    weight: "110 lbs",
    primary_language: "English",
    school: "Oakville High School",
    grade: "9th Grade",
    ssn_last_four: "9156"
  },
  {
    id: "demo_profile_jake",
    user_id: "demo_user_carter",
    name: "Jake Carter",
    relationship: "son",
    type: "child",
    date_of_birth: "2013-11-20",
    blood_type: "O+",
    height: "4'9\"",
    weight: "85 lbs",
    primary_language: "English",
    school: "Lincoln Elementary School",
    grade: "5th Grade",
    ssn_last_four: "3287"
  }
];

// ============= MEDICAL PROFILE (Primary - Michael) =============

export const DEMO_MEDICAL_PROFILE = {
  user_id: "demo_user_carter",
  full_name: "Michael Carter",
  date_of_birth: "1979-06-15",
  blood_type: "O+",
  height: "5'11\"",
  weight: "195 lbs",
  primary_language: "English",
  organ_donor: true,
  emergency_notes: "Wife Emily is primary emergency contact at (555) 111-2222. Children Emma (14) and Jake (11) - Emma can reach us at school (555) 555-1234. Severe penicillin allergy - REQUIRES ALTERNATIVE ANTIBIOTICS."
};

// ============= ALLERGIES =============

export const DEMO_ALLERGIES = [
  {
    id: "demo_allergy_001",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "Penicillin",
    severity: "severe",
    reaction: "Anaphylaxis - throat swelling, difficulty breathing, hives. CARRY EPIPEN. Use Azithromycin or Fluoroquinolones as antibiotic alternatives.",
    diagnosed_by: "Dr. Robert Chen",
    diagnosed_date: "2005-03-15"
  },
  {
    id: "demo_allergy_002",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emily",
    profile_name: "Emily Carter",
    name: "Shellfish (Shrimp, Crab, Lobster)",
    severity: "moderate",
    reaction: "Hives, facial swelling, nausea, stomach cramps within 30 minutes. Benadryl helps for mild reactions.",
    diagnosed_by: "Dr. Sarah Martinez",
    diagnosed_date: "2010-07-22"
  },
  {
    id: "demo_allergy_003",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emma",
    profile_name: "Emma Carter",
    name: "Peanuts",
    severity: "severe",
    reaction: "Anaphylaxis - carries EpiPen Jr. at all times. School nurse has backup. Avoid all tree nuts as precaution.",
    diagnosed_by: "Dr. Amanda Foster",
    diagnosed_date: "2014-02-10"
  },
  {
    id: "demo_allergy_004",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emma",
    profile_name: "Emma Carter",
    name: "Latex",
    severity: "mild",
    reaction: "Contact dermatitis - itchy rash. Use non-latex gloves at dentist/doctor.",
    diagnosed_by: "Dr. Amanda Foster",
    diagnosed_date: "2019-05-18"
  },
  {
    id: "demo_allergy_005",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_jake",
    profile_name: "Jake Carter",
    name: "Seasonal Pollen (Grass, Ragweed)",
    severity: "mild",
    reaction: "Sneezing, runny nose, itchy eyes. Zyrtec daily during spring/fall. No food allergies.",
    diagnosed_by: "Dr. Amanda Foster",
    diagnosed_date: "2020-04-15"
  }
];

// ============= MEDICATIONS =============

export const DEMO_MEDICATIONS = [
  // Michael's medications
  {
    id: "demo_med_001",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "Lisinopril",
    dosage: "10 mg",
    frequency: "Once daily (morning with breakfast)",
    purpose: "Blood pressure management - Hypertension",
    reason: "Blood pressure management - Hypertension",
    prescribing_doctor: "Dr. Robert Chen",
    prescriber_phone: "(555) 234-5678",
    pharmacy: "CVS Pharmacy - 123 Oak Street",
    pharmacy_phone: "(555) 456-7890",
    refills_remaining: 3,
    start_date: "2022-03-15",
    next_refill: getDateDaysFromNow(14),
    is_active: true
  },
  {
    id: "demo_med_002",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "Metformin",
    dosage: "500 mg",
    frequency: "Twice daily (with meals)",
    purpose: "Type 2 Diabetes - Blood sugar control",
    reason: "Type 2 Diabetes - Blood sugar control",
    prescribing_doctor: "Dr. Robert Chen",
    prescriber_phone: "(555) 234-5678",
    pharmacy: "CVS Pharmacy - 123 Oak Street",
    pharmacy_phone: "(555) 456-7890",
    refills_remaining: 2,
    start_date: "2023-01-10",
    next_refill: getDateDaysFromNow(21),
    is_active: true
  },
  {
    id: "demo_med_003",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "EpiPen (Epinephrine)",
    dosage: "0.3 mg",
    frequency: "Emergency use only - Penicillin allergy",
    purpose: "Anaphylaxis emergency treatment",
    reason: "Anaphylaxis emergency treatment",
    prescribing_doctor: "Dr. Robert Chen",
    notes: "Carry at ALL times. Check expiration monthly. Current exp: " + getDateDaysFromNow(180),
    is_active: true
  },
  // Emily's medications
  {
    id: "demo_med_004",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emily",
    profile_name: "Emily Carter",
    name: "Synthroid (Levothyroxine)",
    dosage: "75 mcg",
    frequency: "Once daily (morning, empty stomach, 30 min before food)",
    purpose: "Hypothyroidism - Thyroid hormone replacement",
    reason: "Hypothyroidism - Thyroid hormone replacement",
    prescribing_doctor: "Dr. Sarah Martinez",
    prescriber_phone: "(555) 345-6789",
    pharmacy: "Walgreens - 456 Main Street",
    pharmacy_phone: "(555) 567-8901",
    refills_remaining: 5,
    start_date: "2019-08-20",
    next_refill: getDateDaysFromNow(28),
    is_active: true,
    notes: "Annual TSH blood test required. Next test scheduled for " + getDateDaysFromNow(60)
  },
  {
    id: "demo_med_005",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emily",
    profile_name: "Emily Carter",
    name: "Vitamin D3",
    dosage: "2000 IU",
    frequency: "Once daily",
    purpose: "Vitamin D deficiency supplement",
    reason: "Vitamin D deficiency supplement",
    prescribing_doctor: "Dr. Sarah Martinez",
    start_date: "2021-01-01",
    is_active: true,
    notes: "Over-the-counter. Take with fatty food for better absorption."
  },
  // Emma's medications
  {
    id: "demo_med_006",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emma",
    profile_name: "Emma Carter",
    name: "EpiPen Jr. (Epinephrine)",
    dosage: "0.15 mg",
    frequency: "Emergency use only - Peanut allergy",
    purpose: "Anaphylaxis emergency treatment",
    reason: "Anaphylaxis emergency treatment",
    prescribing_doctor: "Dr. Amanda Foster",
    prescriber_phone: "(555) 456-7890",
    notes: "Carries one in backpack, one at school nurse office, one at home. Check expiration monthly.",
    is_active: true
  },
  {
    id: "demo_med_007",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emma",
    profile_name: "Emma Carter",
    name: "Albuterol Inhaler",
    dosage: "90 mcg/spray",
    frequency: "As needed for asthma symptoms, 2 puffs every 4-6 hours",
    purpose: "Mild asthma - Exercise-induced and seasonal",
    reason: "Mild asthma - Exercise-induced and seasonal",
    prescribing_doctor: "Dr. Amanda Foster",
    prescriber_phone: "(555) 456-7890",
    pharmacy: "CVS Pharmacy - 123 Oak Street",
    refills_remaining: 4,
    start_date: "2020-09-01",
    is_active: true,
    notes: "Use before PE class and sports. Peak flow meter at home - green zone is 350+."
  },
  // Jake's medications
  {
    id: "demo_med_008",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_jake",
    profile_name: "Jake Carter",
    name: "Zyrtec (Cetirizine)",
    dosage: "10 mg",
    frequency: "Once daily during allergy season (March-May, August-October)",
    purpose: "Seasonal allergies - Pollen",
    reason: "Seasonal allergies - Pollen",
    prescribing_doctor: "Dr. Amanda Foster",
    start_date: "2020-04-15",
    is_active: true,
    notes: "Over-the-counter. Take in evening to avoid drowsiness during school."
  }
];

// ============= CONDITIONS =============

export const DEMO_CONDITIONS = [
  {
    id: "demo_cond_001",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "Hypertension (High Blood Pressure)",
    condition: "Hypertension (High Blood Pressure)",
    diagnosed_date: "2022-03-01",
    diagnosing_doctor: "Dr. Robert Chen",
    status: "managed",
    notes: "Stage 1 hypertension. Well controlled with Lisinopril and lifestyle changes. Monitor BP weekly - target below 130/80. Lost 15 lbs since diagnosis."
  },
  {
    id: "demo_cond_002",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "Type 2 Diabetes",
    condition: "Type 2 Diabetes",
    diagnosed_date: "2023-01-10",
    diagnosing_doctor: "Dr. Robert Chen",
    status: "managed",
    notes: "Pre-diabetes progressed to Type 2. A1C at diagnosis: 7.2%, current: 6.4%. Managing with Metformin, diet, and exercise. Check blood sugar fasting and 2hrs after meals."
  },
  {
    id: "demo_cond_003",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "Penicillin Allergy",
    condition: "Penicillin Allergy",
    diagnosed_date: "2005-03-15",
    diagnosing_doctor: "Dr. James Wilson",
    status: "active",
    notes: "SEVERE - Anaphylaxis risk. Red medical alert bracelet worn daily. All healthcare providers must be notified. Alternative antibiotics: Azithromycin, Fluoroquinolones."
  },
  {
    id: "demo_cond_004",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emily",
    profile_name: "Emily Carter",
    name: "Hypothyroidism",
    condition: "Hypothyroidism",
    diagnosed_date: "2019-08-01",
    diagnosing_doctor: "Dr. Sarah Martinez",
    status: "managed",
    notes: "Hashimoto's thyroiditis. Stable on Synthroid 75mcg. Annual TSH monitoring - last level 2.4 (normal). No symptoms when compliant with medication."
  },
  {
    id: "demo_cond_005",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emma",
    profile_name: "Emma Carter",
    name: "Peanut Allergy",
    condition: "Peanut Allergy",
    diagnosed_date: "2014-02-10",
    diagnosing_doctor: "Dr. Amanda Foster",
    status: "active",
    notes: "SEVERE - Anaphylaxis risk. Carries EpiPen at all times. School has 504 plan on file. Avoid all peanuts and tree nuts. Can eat sunflower seed butter."
  },
  {
    id: "demo_cond_006",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emma",
    profile_name: "Emma Carter",
    name: "Mild Asthma",
    condition: "Mild Asthma",
    diagnosed_date: "2020-09-01",
    diagnosing_doctor: "Dr. Amanda Foster",
    status: "managed",
    notes: "Exercise-induced and seasonal triggers. Uses Albuterol inhaler before sports and during pollen season. Peak flow monitoring at home. No overnight hospitalizations."
  },
  {
    id: "demo_cond_007",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_jake",
    profile_name: "Jake Carter",
    name: "Seasonal Allergies",
    condition: "Seasonal Allergies",
    diagnosed_date: "2020-04-15",
    diagnosing_doctor: "Dr. Amanda Foster",
    status: "managed",
    notes: "Grass and ragweed pollen. Manages well with daily Zyrtec during peak seasons. No food allergies identified."
  }
];

// ============= SURGERIES =============

export const DEMO_SURGERIES = [
  {
    id: "demo_surg_001",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "Gastric Sleeve (Sleeve Gastrectomy)",
    procedure: "Gastric Sleeve (Sleeve Gastrectomy)",
    date: "2021-06-15",
    hospital: "Oakville Medical Center",
    facility: "Oakville Medical Center",
    surgeon: "Dr. Richard Thompson",
    surgeon_phone: "(555) 789-0123",
    anesthesia: "General",
    notes: "Weight loss surgery. Lost 80 lbs over 18 months. No complications. Follow bariatric diet guidelines. Annual vitamin B12 and iron checks required."
  },
  {
    id: "demo_surg_002",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "Arthroscopic Knee Surgery (Right)",
    procedure: "Arthroscopic Knee Surgery (Right)",
    date: "2018-11-03",
    hospital: "Oakville Orthopedic Center",
    facility: "Oakville Orthopedic Center",
    surgeon: "Dr. Mark Stevens",
    surgeon_phone: "(555) 890-1234",
    anesthesia: "Local with sedation",
    notes: "Meniscus repair from basketball injury. Full recovery in 8 weeks. PT completed. No ongoing issues."
  },
  {
    id: "demo_surg_003",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_michael",
    profile_name: "Michael Carter",
    name: "Appendectomy",
    procedure: "Appendectomy",
    date: "2008-04-22",
    hospital: "Memorial General Hospital",
    facility: "Memorial General Hospital",
    surgeon: "Dr. James Wilson",
    anesthesia: "General",
    notes: "Laparoscopic procedure. Emergency surgery for acute appendicitis. No complications, full recovery."
  },
  {
    id: "demo_surg_004",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emily",
    profile_name: "Emily Carter",
    name: "C-Section (Jake)",
    procedure: "C-Section (Jake)",
    date: "2013-11-20",
    hospital: "St. Mary's Medical Center",
    facility: "St. Mary's Medical Center",
    surgeon: "Dr. Patricia Adams",
    surgeon_phone: "(555) 901-2345",
    anesthesia: "Spinal",
    notes: "Planned C-section due to breech position. Healthy delivery, 8 lbs 2 oz. Normal postpartum recovery."
  },
  {
    id: "demo_surg_005",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emily",
    profile_name: "Emily Carter",
    name: "Thyroid Biopsy (Fine Needle Aspiration)",
    procedure: "Thyroid Biopsy (Fine Needle Aspiration)",
    date: "2019-07-15",
    hospital: "Oakville Medical Center",
    facility: "Oakville Medical Center",
    surgeon: "Dr. Sarah Martinez",
    anesthesia: "Local",
    notes: "Biopsy of thyroid nodule. Results: Benign. Annual ultrasound monitoring - nodule stable at 8mm."
  },
  {
    id: "demo_surg_006",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_emma",
    profile_name: "Emma Carter",
    name: "Tonsillectomy & Adenoidectomy",
    procedure: "Tonsillectomy & Adenoidectomy",
    date: "2019-07-15",
    hospital: "Children's Hospital of Oakville",
    facility: "Children's Hospital of Oakville",
    surgeon: "Dr. Kevin Lee",
    surgeon_phone: "(555) 012-3456",
    anesthesia: "General",
    notes: "Outpatient procedure for recurrent strep throat (5 episodes in one year). Full recovery in 10 days. No strep since surgery."
  },
  {
    id: "demo_surg_007",
    user_id: "demo_user_carter",
    profile_id: "demo_profile_jake",
    profile_name: "Jake Carter",
    name: "Ear Tubes (Bilateral Myringotomy)",
    procedure: "Ear Tubes (Bilateral Myringotomy)",
    date: "2016-03-10",
    hospital: "Children's Hospital of Oakville",
    facility: "Children's Hospital of Oakville",
    surgeon: "Dr. Kevin Lee",
    anesthesia: "General",
    notes: "For chronic ear infections (8 infections in 12 months). Tubes fell out naturally at age 5. No ear infections since."
  }
];

// ============= DOCTORS =============

export const DEMO_DOCTORS = [
  {
    id: "demo_doc_001",
    user_id: "demo_user_carter",
    name: "Dr. Robert Chen, MD",
    specialty: "Internal Medicine",
    practice: "Family Health Partners",
    clinic: "Family Health Partners",
    phone: "(555) 234-5678",
    fax: "(555) 234-5679",
    address: "456 Medical Plaza, Suite 200, Oakville, ST 12345",
    email: "drchen@familyhealthpartners.com",
    is_primary: true,
    portal_url: "https://familyhealthpartners.com/portal",
    insurance_accepted: "BlueCross BlueShield, Aetna, United",
    notes: "Michael's primary care physician since 2015. Manages diabetes and hypertension. Annual physicals in March."
  },
  {
    id: "demo_doc_002",
    user_id: "demo_user_carter",
    name: "Dr. Sarah Martinez, MD",
    specialty: "Endocrinology",
    practice: "Thyroid & Diabetes Specialists",
    clinic: "Thyroid & Diabetes Specialists",
    phone: "(555) 345-6789",
    fax: "(555) 345-6780",
    address: "789 Health Center Dr, Suite 100, Oakville, ST 12345",
    email: "smartinez@endospecialists.com",
    is_primary: false,
    portal_url: "https://endospecialists.com/patient",
    insurance_accepted: "BlueCross BlueShield, Cigna",
    notes: "Emily's thyroid specialist. Also consults on Michael's diabetes. Very thorough, explains things well."
  },
  {
    id: "demo_doc_003",
    user_id: "demo_user_carter",
    name: "Dr. Amanda Foster, MD, FAAP",
    specialty: "Pediatrics",
    practice: "Sunshine Pediatrics",
    clinic: "Sunshine Pediatrics",
    phone: "(555) 456-7890",
    fax: "(555) 456-7891",
    emergency_line: "(555) 456-7899",
    address: "321 Kids Care Lane, Oakville, ST 12345",
    email: "afoster@sunshinepeds.com",
    is_primary: false,
    portal_url: "https://sunshinepeds.com/portal",
    insurance_accepted: "All major insurers",
    notes: "Emma and Jake's pediatrician since birth. Manages Emma's asthma and peanut allergy. Nurse line available 24/7."
  },
  {
    id: "demo_doc_004",
    user_id: "demo_user_carter",
    name: "Dr. Lisa Park, DDS",
    specialty: "Family Dentistry",
    practice: "Bright Smile Family Dental",
    clinic: "Bright Smile Family Dental",
    phone: "(555) 567-8901",
    fax: "(555) 567-8902",
    address: "100 Dental Drive, Oakville, ST 12345",
    is_primary: false,
    insurance_accepted: "Delta Dental, most PPO plans",
    notes: "Whole family sees Dr. Park. Cleanings every 6 months. Emma has braces with Dr. Kim (orthodontist in same building)."
  },
  {
    id: "demo_doc_005",
    user_id: "demo_user_carter",
    name: "Dr. William Hayes, OD",
    specialty: "Optometry",
    practice: "Clear Vision Eye Care",
    clinic: "Clear Vision Eye Care",
    phone: "(555) 678-9012",
    address: "555 Vision Way, Oakville, ST 12345",
    is_primary: false,
    insurance_accepted: "VSP, EyeMed",
    notes: "Annual eye exams for family. Michael and Emily wear glasses. Kids have 20/20 vision."
  },
  {
    id: "demo_doc_006",
    user_id: "demo_user_carter",
    name: "Dr. Mark Stevens, MD",
    specialty: "Orthopedic Surgery",
    practice: "Oakville Orthopedic Center",
    clinic: "Oakville Orthopedic Center",
    phone: "(555) 890-1234",
    address: "200 Bone & Joint Way, Oakville, ST 12345",
    is_primary: false,
    notes: "Performed Michael's knee surgery in 2018. See for any sports injuries."
  },
  {
    id: "demo_doc_007",
    user_id: "demo_user_carter",
    name: "Dr. Jennifer Woods, DVM",
    specialty: "Veterinary Medicine",
    practice: "Oakville Animal Hospital",
    clinic: "Oakville Animal Hospital",
    phone: "(555) 789-0123",
    emergency_line: "(555) 789-0199",
    address: "200 Pet Care Lane, Oakville, ST 12345",
    is_primary: false,
    notes: "Buddy's vet since he was a puppy. Open Saturdays. Emergency vet hospital referral: Oakville Emergency Vet (555) 999-PETS"
  }
];

// ============= INSURANCE =============

export const DEMO_INSURANCE = [
  {
    id: "demo_ins_001",
    user_id: "demo_user_carter",
    provider: "BlueCross BlueShield",
    plan_name: "Family PPO Gold Plan",
    policy_number: "BCB-987654321",
    group_number: "GRP-TECHCORP-2024",
    member_id: "XYZ123456789",
    subscriber: "Michael Carter",
    phone: "1-800-555-BLUE (2583)",
    claims_address: "P.O. Box 105568, Atlanta, GA 30348",
    coverage_type: "medical",
    is_primary: true,
    deductible: "$1,500 individual / $3,000 family",
    copay: "$25 PCP / $50 Specialist / $100 ER",
    out_of_pocket_max: "$6,000 individual / $12,000 family",
    notes: "Through Michael's employer TechCorp Inc. Covers all 4 family members. Preventive care 100% covered. Prior auth required for MRI, CT, surgery."
  },
  {
    id: "demo_ins_002",
    user_id: "demo_user_carter",
    provider: "Delta Dental",
    plan_name: "Family Dental Plus",
    policy_number: "DD-456789123",
    group_number: "DENT-TECHCORP",
    member_id: "DD-789456123",
    subscriber: "Michael Carter",
    phone: "1-800-555-DENT (3368)",
    coverage_type: "dental",
    is_primary: true,
    deductible: "$50 individual / $150 family",
    notes: "2 cleanings per year covered 100%. Basic services 80%, major 50%. Orthodontia 50% up to $2,000 lifetime max (Emma's braces)."
  },
  {
    id: "demo_ins_003",
    user_id: "demo_user_carter",
    provider: "VSP Vision Care",
    plan_name: "Family Vision Plus",
    policy_number: "VSP-321654987",
    member_id: "VSP-654321789",
    subscriber: "Michael Carter",
    phone: "1-800-555-EYES (3937)",
    coverage_type: "vision",
    is_primary: true,
    notes: "$150 frame allowance per year. Exam copay $10. Contacts allowance $130/year. Use in-network providers for best coverage."
  },
  {
    id: "demo_ins_004",
    user_id: "demo_user_carter",
    provider: "MetLife",
    plan_name: "Group Life Insurance",
    policy_number: "ML-LIFE-789456",
    subscriber: "Michael Carter",
    phone: "1-800-555-LIFE (5433)",
    coverage_type: "life",
    is_primary: true,
    notes: "Through TechCorp. 2x annual salary ($240,000 coverage). Beneficiary: Emily Carter. Supplemental $100,000 policy purchased separately."
  }
];

// ============= EMERGENCY CONTACTS =============

export const DEMO_EMERGENCY_CONTACTS = [
  {
    id: "demo_ec_001",
    user_id: "demo_user_carter",
    name: "Emily Carter",
    relationship: "Spouse",
    phone: "(555) 111-2222",
    phone_secondary: "(555) 111-3333",
    email: "emily.carter@email.com",
    address: "742 Maple Street, Oakville, ST 12345",
    is_primary: true,
    can_make_medical_decisions: true,
    notes: "Wife - Cell first, then work at Oakville Library. Has medical power of attorney."
  },
  {
    id: "demo_ec_002",
    user_id: "demo_user_carter",
    name: "Margaret Carter",
    relationship: "Mother",
    phone: "(555) 333-4444",
    phone_secondary: "(555) 333-4445",
    email: "margaret.carter@email.com",
    address: "456 Oak Avenue, Oakville, ST 12345",
    is_primary: false,
    can_pickup_children: true,
    notes: "Michael's mother. Lives 20 min away. Retired, available anytime. Authorized to pick up kids from school."
  },
  {
    id: "demo_ec_003",
    user_id: "demo_user_carter",
    name: "David Reynolds",
    relationship: "Brother-in-law",
    phone: "(555) 444-5555",
    email: "david.reynolds@email.com",
    address: "890 Pine Street, Oakville, ST 12345",
    is_primary: false,
    can_pickup_children: true,
    notes: "Emily's brother. Backup for kids. Has key to house. Kids know him well."
  },
  {
    id: "demo_ec_004",
    user_id: "demo_user_carter",
    name: "Susan Miller",
    relationship: "Neighbor / Friend",
    phone: "(555) 222-3333",
    address: "744 Maple Street, Oakville, ST 12345",
    is_primary: false,
    can_pickup_children: true,
    notes: "Next door neighbor. Has spare house key. Kids can go there in any emergency. Her daughter is Emma's friend."
  },
  {
    id: "demo_ec_005",
    user_id: "demo_user_carter",
    name: "Oakville High School",
    relationship: "Emma's School",
    phone: "(555) 555-9000",
    phone_secondary: "(555) 555-9001",
    address: "500 Education Way, Oakville, ST 12345",
    is_primary: false,
    notes: "Main office & attendance. School nurse: Ext. 150. Emma's counselor: Ms. Williams, Ext. 225."
  },
  {
    id: "demo_ec_006",
    user_id: "demo_user_carter",
    name: "Lincoln Elementary School",
    relationship: "Jake's School",
    phone: "(555) 555-1234",
    phone_secondary: "(555) 555-1235",
    address: "100 Learning Lane, Oakville, ST 12345",
    is_primary: false,
    notes: "Main office & nurse's office. Jake's teacher: Mrs. Thompson, Room 204. Dismissal at 3:15 PM."
  }
];

// ============= PETS (Buddy the Golden Retriever) =============

export const DEMO_PETS = [
  {
    id: "demo_pet_001",
    user_id: "demo_user_carter",
    name: "Buddy",
    species: "Dog",
    breed: "Golden Retriever",
    color: "Golden/Cream",
    birth_date: "2019-05-15",
    weight: "75 lbs",
    microchip_id: "985112345678901",
    license_number: "OAK-DOG-2024-4521",
    rabies_tag: "RAB-2024-7890",
    vet_name: "Dr. Jennifer Woods",
    vet_phone: "(555) 789-0123",
    vet_clinic: "Oakville Animal Hospital",
    vet_address: "200 Pet Care Lane, Oakville, ST 12345",
    emergency_vet: "Oakville Emergency Vet - (555) 999-PETS",
    photo: null,
    medications: "Heartgard Plus monthly (heartworm), NexGard monthly (flea/tick)",
    allergies: "None known",
    feeding: "Blue Buffalo Adult Large Breed - 2 cups morning, 2 cups evening",
    notes: "Friendly with everyone, especially kids. Knows basic commands. Anxious during thunderstorms - calming treats in pantry. Loves car rides. Gets along with other dogs."
  }
];

// ============= VEHICLES =============

export const DEMO_VEHICLES = [
  {
    id: "demo_vehicle_001",
    user_id: "demo_user_carter",
    make: "Toyota",
    model: "Highlander XLE",
    year: 2022,
    color: "Midnight Black Metallic",
    vin: "5TDZA23C52S123456",
    license_plate: "ABC-1234",
    registration_exp: getDateDaysFromNow(180),
    mileage: 32500,
    insurance_provider: "State Farm Auto",
    policy_number: "SF-AUTO-789456123",
    agent_name: "Tom Bradley",
    agent_phone: "(555) 222-3456",
    notes: "Family SUV - Michael's primary vehicle. Leather seats, 3rd row. Car seats: none needed anymore. Keep emergency kit in back."
  },
  {
    id: "demo_vehicle_002",
    user_id: "demo_user_carter",
    make: "Honda",
    model: "CR-V EX-L",
    year: 2021,
    color: "Platinum White Pearl",
    vin: "2HKRW2H53MH654321",
    license_plate: "XYZ-5678",
    registration_exp: getDateDaysFromNow(220),
    mileage: 38000,
    insurance_provider: "State Farm Auto",
    policy_number: "SF-AUTO-789456124",
    agent_name: "Tom Bradley",
    agent_phone: "(555) 222-3456",
    notes: "Emily's car - used for school runs and library commute. Great on gas. Roof rack for bikes."
  }
];

// ============= MAINTENANCE RECORDS =============

export const DEMO_MAINTENANCE_RECORDS = [
  {
    id: "demo_maint_001",
    vehicle_id: "demo_vehicle_001",
    user_id: "demo_user_carter",
    type: "Oil Change & Filter",
    date: getPastDate(15),
    mileage: 32500,
    cost: 89.99,
    provider: "Toyota of Oakville",
    provider_phone: "(555) 333-AUTO",
    notes: "Full synthetic 0W-20. Next service at 37,500 miles or 6 months. Also rotated tires."
  },
  {
    id: "demo_maint_002",
    vehicle_id: "demo_vehicle_001",
    user_id: "demo_user_carter",
    type: "Tire Rotation & Inspection",
    date: getPastDate(15),
    mileage: 32500,
    cost: 0,
    provider: "Toyota of Oakville",
    notes: "Included with oil change. All tires at 7/32\" tread depth. Alignment good."
  },
  {
    id: "demo_maint_003",
    vehicle_id: "demo_vehicle_001",
    user_id: "demo_user_carter",
    type: "30,000 Mile Service",
    date: getPastDate(90),
    mileage: 30000,
    cost: 425.00,
    provider: "Toyota of Oakville",
    notes: "Major service - transmission fluid, brake fluid, cabin filter, engine air filter, spark plugs inspected."
  },
  {
    id: "demo_maint_004",
    vehicle_id: "demo_vehicle_002",
    user_id: "demo_user_carter",
    type: "Oil Change",
    date: getPastDate(30),
    mileage: 38000,
    cost: 75.00,
    provider: "Jiffy Lube - Main Street",
    notes: "Synthetic blend. Next due at 43,000 miles. Air filter replaced ($25 extra)."
  },
  {
    id: "demo_maint_005",
    vehicle_id: "demo_vehicle_002",
    user_id: "demo_user_carter",
    type: "Brake Pad Replacement (Front)",
    date: getPastDate(60),
    mileage: 36000,
    cost: 289.00,
    provider: "Honda of Oakville",
    notes: "Front pads worn to 2mm. Rotors still good - resurfaced. Rear pads at 5mm - good for another 20k miles."
  },
  {
    id: "demo_maint_006",
    vehicle_id: "demo_vehicle_002",
    user_id: "demo_user_carter",
    type: "New Tires (All 4)",
    date: getPastDate(180),
    mileage: 30000,
    cost: 680.00,
    provider: "Discount Tire",
    notes: "Michelin Defender T+H 225/65R17. Lifetime rotation included. 60,000 mile warranty."
  }
];

// ============= PARKING LOCATIONS =============

export const DEMO_PARKING_LOCATIONS = [
  {
    id: "demo_parking_001",
    user_id: "demo_user_carter",
    title: "Oakville Mall - Parking Structure Level 2",
    address: "123 Main Street, Oakville",
    section: "Blue Zone",
    spot_number: "B-147",
    vehicle_id: null,
    parked_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    photo: null,
    notes: "Near Macy's entrance, by the elevator. Good cell signal here.",
    status: "active"
  }
];

// ============= IMPORTANT DATES =============

export const DEMO_IMPORTANT_DATES = [
  // Family Events
  {
    id: "demo_date_001",
    user_id: "demo_user_carter",
    category: "family",
    title: "Emma's Soccer Tournament",
    date: getDateDaysFromNow(7),
    time: "08:00",
    location: "Oakville Sports Complex - Field 3",
    notes: "Varsity team tournament. Games at 8am, 11am, 2pm (if they advance). Pack cooler, chairs, sunscreen. Carpool with the Millers.",
    reminder: true
  },
  {
    id: "demo_date_002",
    user_id: "demo_user_carter",
    category: "family",
    title: "Jake's Science Fair",
    date: getDateDaysFromNow(14),
    time: "18:00",
    location: "Lincoln Elementary Gymnasium",
    notes: "Jake's volcano project! Arrive by 5:30 for setup. Grandma Margaret coming. Pizza after at Luigi's.",
    reminder: true
  },
  {
    id: "demo_date_003",
    user_id: "demo_user_carter",
    category: "family",
    title: "Family Game Night",
    date: getDateDaysFromNow(3),
    time: "19:00",
    location: "Home",
    notes: "Emma wants to play Ticket to Ride. Order Chinese food.",
    reminder: true
  },
  
  // Birthdays
  {
    id: "demo_date_004",
    user_id: "demo_user_carter",
    category: "birthday",
    title: "Emma's 15th Birthday",
    date: "2025-03-08",
    notes: "Turning 15! Wants a sleepover with 4 friends. Gift ideas: New phone case, art supplies, Spotify gift card.",
    reminder: true
  },
  {
    id: "demo_date_005",
    user_id: "demo_user_carter",
    category: "birthday",
    title: "Emily's Birthday",
    date: "2025-09-22",
    notes: "Planning surprise dinner at La Bella Italia. Gift: Spa day voucher from Serenity Spa. Kids making card.",
    reminder: true
  },
  {
    id: "demo_date_006",
    user_id: "demo_user_carter",
    category: "birthday",
    title: "Jake's 12th Birthday",
    date: "2025-11-20",
    notes: "Turning 12! Wants a laser tag party at Fun Zone (8 friends). Gift: New basketball, video game.",
    reminder: true
  },
  {
    id: "demo_date_007",
    user_id: "demo_user_carter",
    category: "birthday",
    title: "Michael's Birthday",
    date: "2025-06-15",
    notes: "Turning 46. Keep it low-key - nice dinner and cake. Emily planning something?",
    reminder: true
  },
  {
    id: "demo_date_008",
    user_id: "demo_user_carter",
    category: "birthday",
    title: "Grandma Margaret's Birthday",
    date: "2025-04-12",
    notes: "Turning 72. Sunday brunch at her favorite restaurant (The Garden Cafe). Flowers delivered to her house.",
    reminder: true
  },
  {
    id: "demo_date_009",
    user_id: "demo_user_carter",
    category: "birthday",
    title: "Wedding Anniversary",
    date: "2025-08-18",
    notes: "17th anniversary! Babysitter booked (Grandma). Dinner at The Capital Grille - reservation made.",
    reminder: true
  },
  
  // Appointments
  {
    id: "demo_date_010",
    user_id: "demo_user_carter",
    category: "appointment",
    title: "Michael - Annual Physical",
    date: getDateDaysFromNow(21),
    time: "09:00",
    location: "Family Health Partners",
    provider: "Dr. Robert Chen",
    notes: "Fasting required for blood work (nothing after midnight). Bring medication list. Discuss knee pain.",
    reminder: true
  },
  {
    id: "demo_date_011",
    user_id: "demo_user_carter",
    category: "appointment",
    title: "Emily - Thyroid Check",
    date: getDateDaysFromNow(35),
    time: "14:00",
    location: "Thyroid & Diabetes Specialists",
    provider: "Dr. Sarah Martinez",
    notes: "Annual TSH blood work. Take Synthroid as normal (no need to skip). Discuss fatigue.",
    reminder: true
  },
  {
    id: "demo_date_012",
    user_id: "demo_user_carter",
    category: "appointment",
    title: "Kids - Dental Cleanings",
    date: getDateDaysFromNow(10),
    time: "15:30",
    location: "Bright Smile Family Dental",
    provider: "Dr. Lisa Park",
    notes: "Emma at 3:30, Jake at 4:00. Back-to-back appointments. Emma's braces check included.",
    reminder: true
  },
  {
    id: "demo_date_013",
    user_id: "demo_user_carter",
    category: "appointment",
    title: "Emma - Asthma Check-up",
    date: getDateDaysFromNow(45),
    time: "10:00",
    location: "Sunshine Pediatrics",
    provider: "Dr. Amanda Foster",
    notes: "Routine asthma management. Bring peak flow log. Refill inhaler prescription.",
    reminder: true
  },
  {
    id: "demo_date_014",
    user_id: "demo_user_carter",
    category: "appointment",
    title: "Buddy - Annual Vaccines",
    date: getDateDaysFromNow(30),
    time: "11:00",
    location: "Oakville Animal Hospital",
    provider: "Dr. Jennifer Woods",
    notes: "Rabies, DHPP, Bordetella boosters. Bring vaccination records. Ask about dental cleaning.",
    reminder: true
  },
  
  // Reservations
  {
    id: "demo_date_015",
    user_id: "demo_user_carter",
    category: "reservation",
    title: "Anniversary Dinner",
    date: getDateDaysFromNow(25),
    time: "19:00",
    location: "The Capital Grille",
    confirmation_number: "CG-2024-CARTER-8821",
    party_size: 2,
    notes: "17th wedding anniversary! Window table requested. Grandma Margaret babysitting.",
    reminder: true
  },
  {
    id: "demo_date_016",
    user_id: "demo_user_carter",
    category: "reservation",
    title: "Family Vacation - Lake Tahoe",
    date: getDateDaysFromNow(60),
    end_date: getDateDaysFromNow(67),
    location: "Mountain View Cabin Resort",
    confirmation_number: "MVLC-789456",
    notes: "Cabin #12, 3BR/2BA. Check-in 3pm, Check-out 11am. DOG FRIENDLY! Pack hiking gear, fishing poles. Kayak rental booked for Day 3.",
    reminder: true
  },
  {
    id: "demo_date_017",
    user_id: "demo_user_carter",
    category: "reservation",
    title: "Emma's Birthday Dinner",
    date: "2025-03-08",
    time: "18:00",
    location: "Olive Garden",
    confirmation_number: "OG-030825-CARTER",
    party_size: 6,
    notes: "After sleepover, dinner with family. Requested booth. Cake from Sweetie Pies Bakery (pick up at 5pm).",
    reminder: true
  },
  
  // Memberships
  {
    id: "demo_date_018",
    user_id: "demo_user_carter",
    category: "membership",
    title: "Gym Membership Renewal",
    date: getDateDaysFromNow(45),
    provider: "FitLife Gym",
    cost: 99.99,
    notes: "Family plan - Michael & Emily. Auto-renews monthly. Cancel by the 15th to stop.",
    reminder: true
  },
  {
    id: "demo_date_019",
    user_id: "demo_user_carter",
    category: "membership",
    title: "Costco Executive Membership",
    date: getDateDaysFromNow(90),
    provider: "Costco",
    cost: 130.00,
    notes: "Annual renewal. Executive level gets 2% cash back. Last year earned $145 back.",
    reminder: true
  },
  {
    id: "demo_date_020",
    user_id: "demo_user_carter",
    category: "membership",
    title: "AAA Plus Membership",
    date: getDateDaysFromNow(120),
    provider: "AAA",
    cost: 125.00,
    notes: "Plus level - 100 mile towing. Covers both cars. Used twice last year (flat tire, dead battery).",
    reminder: true
  },
  {
    id: "demo_date_021",
    user_id: "demo_user_carter",
    category: "membership",
    title: "Amazon Prime Renewal",
    date: getDateDaysFromNow(200),
    provider: "Amazon",
    cost: 139.00,
    notes: "Annual renewal on credit card. Includes Prime Video. Student discount ended when Emma aged out.",
    reminder: true
  }
];

// ============= CONTACTS =============

export const DEMO_CONTACTS = [
  // Family
  {
    id: "demo_contact_001",
    user_id: "demo_user_carter",
    category: "family",
    name: "Emily Carter",
    relationship: "Wife",
    phone: "(555) 111-2222",
    phone_work: "(555) 111-3333",
    email: "emily.carter@email.com",
    address: "742 Maple Street, Oakville, ST 12345",
    notes: "Works at Oakville Public Library. Off on Sundays and Mondays."
  },
  {
    id: "demo_contact_002",
    user_id: "demo_user_carter",
    category: "family",
    name: "Margaret Carter",
    relationship: "Mother",
    phone: "(555) 333-4444",
    email: "margaret.carter@email.com",
    address: "456 Oak Avenue, Oakville, ST 12345",
    birthday: "April 12",
    notes: "Michael's mom. Retired teacher. Available for kid emergencies. Prefers calls over texts."
  },
  {
    id: "demo_contact_003",
    user_id: "demo_user_carter",
    category: "family",
    name: "Robert Carter Sr.",
    relationship: "Father",
    phone: "(555) 333-4445",
    email: "bob.carter@email.com",
    address: "456 Oak Avenue, Oakville, ST 12345",
    notes: "Michael's dad. Retired electrician. Lives with Margaret. Hard of hearing - text works better."
  },
  {
    id: "demo_contact_004",
    user_id: "demo_user_carter",
    category: "family",
    name: "David Reynolds",
    relationship: "Brother-in-law",
    phone: "(555) 444-5555",
    email: "david.reynolds@email.com",
    address: "890 Pine Street, Oakville, ST 12345",
    notes: "Emily's brother. Software developer. Has house key. Good with the kids."
  },
  
  // Work
  {
    id: "demo_contact_005",
    user_id: "demo_user_carter",
    category: "work",
    name: "TechCorp HR Department",
    phone: "(555) 999-0000",
    email: "hr@techcorp.com",
    notes: "Benefits questions, PTO requests, 401k issues. Open 8am-5pm M-F."
  },
  {
    id: "demo_contact_006",
    user_id: "demo_user_carter",
    category: "work",
    name: "Sarah Johnson",
    relationship: "Manager",
    phone: "(555) 888-1111",
    email: "sarah.johnson@techcorp.com",
    notes: "Michael's direct supervisor. Very understanding about family emergencies. Prefers Slack for non-urgent."
  },
  {
    id: "demo_contact_007",
    user_id: "demo_user_carter",
    category: "work",
    name: "Oakville Public Library",
    phone: "(555) 111-3333",
    email: "info@oakvillelibrary.org",
    address: "200 Library Way, Oakville, ST 12345",
    notes: "Emily's workplace. Ask for Emily Carter or leave message. She checks voicemail hourly."
  },
  
  // Services
  {
    id: "demo_contact_008",
    user_id: "demo_user_carter",
    category: "service",
    name: "State Farm - Tom Bradley",
    relationship: "Insurance Agent",
    phone: "(555) 222-3456",
    email: "tom.bradley@statefarm.com",
    address: "500 Insurance Plaza, Oakville, ST 12345",
    notes: "Home, auto, life insurance agent. Policy reviews annually in January. 24/7 claims: 1-800-SF-CLAIM."
  },
  {
    id: "demo_contact_009",
    user_id: "demo_user_carter",
    category: "service",
    name: "Mike's Plumbing",
    phone: "(555) 911-PIPE",
    notes: "Used for bathroom renovation 2023 and water heater 2024. Fair prices. Emergency service available."
  },
  {
    id: "demo_contact_010",
    user_id: "demo_user_carter",
    category: "service",
    name: "Oakville Electric Company",
    phone: "(555) 246-8000",
    emergency_phone: "(555) 246-9999",
    notes: "24/7 outage line. Account #: 123456789. Auto-pay set up on Chase card."
  },
  {
    id: "demo_contact_011",
    user_id: "demo_user_carter",
    category: "service",
    name: "Green Lawn Care",
    phone: "(555) 456-LAWN",
    email: "service@greenlawn.com",
    notes: "Weekly mowing March-November. $55/visit. Spring cleanup $150. Owner: Rick. Very reliable."
  },
  {
    id: "demo_contact_012",
    user_id: "demo_user_carter",
    category: "service",
    name: "Comfort HVAC",
    phone: "(555) 888-COOL",
    notes: "Furnace and AC service. Annual maintenance contract - $199/year. Filter changes included. Tech: Marcus."
  }
];

// ============= PLACES =============

export const DEMO_PLACES = [
  {
    id: "demo_place_001",
    user_id: "demo_user_carter",
    name: "Oakville Family Medicine",
    category: "medical",
    address: "1234 Healthcare Drive, Oakville, ST 12345",
    phone: "(555) 456-7890",
    notes: "Dr. Sarah Chen's office. Turn left after the main entrance, Suite 201. Free parking lot behind the building.",
    is_favorite: true
  },
  {
    id: "demo_place_002",
    user_id: "demo_user_carter",
    name: "Oakville High School",
    category: "school",
    address: "500 Education Way, Oakville, ST 12345",
    phone: "(555) 789-0123",
    notes: "Emma's school. Main office for pickup. Soccer practice at Field B (behind gym).",
    is_favorite: true
  },
  {
    id: "demo_place_003",
    user_id: "demo_user_carter",
    name: "Lincoln Elementary School",
    category: "school",
    address: "200 Learning Lane, Oakville, ST 12345",
    phone: "(555) 321-0987",
    notes: "Jake's school. After-school pickup at Door C. Parent parking in front lot.",
    is_favorite: true
  },
  {
    id: "demo_place_004",
    user_id: "demo_user_carter",
    name: "Oakville Public Library",
    category: "services",
    address: "100 Main Street, Oakville, ST 12345",
    phone: "(555) 555-READ",
    notes: "Emily works here. Kids' section in basement. Summer reading program registration in June."
  },
  {
    id: "demo_place_005",
    user_id: "demo_user_carter",
    name: "TechCorp Headquarters",
    category: "work",
    address: "1 Innovation Parkway, Oakville, ST 12345",
    phone: "(555) 123-4000",
    notes: "Michael's office. Building C, 3rd floor. Visitor parking requires guest pass from security."
  },
  {
    id: "demo_place_006",
    user_id: "demo_user_carter",
    name: "Costco Wholesale",
    category: "shopping",
    address: "5000 Big Box Boulevard, Oakville, ST 12345",
    phone: "(555) 999-1234",
    notes: "Membership card in wallet. Pharmacy hours: 10am-7pm weekdays. Gas station open 6am-9pm."
  }
];

// ============= REIMBURSEMENTS =============

export const DEMO_REIMBURSEMENTS = [
  {
    id: "demo_reimb_001",
    user_id: "demo_user_carter",
    title: "Emma's Orthodontist Visit",
    amount: 150.00,
    date: getPastDate(15),
    category: "medical",
    status: "pending",
    notes: "Braces adjustment. Insurance claim submitted, awaiting reimbursement.",
    receipt_url: null
  },
  {
    id: "demo_reimb_002",
    user_id: "demo_user_carter",
    title: "Jake's Soccer Cleats",
    amount: 89.99,
    date: getPastDate(30),
    category: "activities",
    status: "submitted",
    notes: "Required for school team. Keep receipt for flexible spending reimbursement.",
    receipt_url: null
  },
  {
    id: "demo_reimb_003",
    user_id: "demo_user_carter",
    title: "Prescription - Lisinopril",
    amount: 25.00,
    date: getPastDate(7),
    category: "medical",
    status: "reimbursed",
    notes: "Monthly blood pressure medication. HSA reimbursement processed.",
    receipt_url: null
  },
  {
    id: "demo_reimb_004",
    user_id: "demo_user_carter",
    title: "Eye Exam - Michael",
    amount: 120.00,
    date: getPastDate(45),
    category: "medical",
    status: "reimbursed",
    notes: "Annual vision check. VSP covered exam, paid copay for new frames.",
    receipt_url: null
  }
];

// ============= DOCUMENTS =============

export const DEMO_DOCUMENTS = [
  // ========== IDs & TRAVEL ==========
  {
    id: "demo_doc_passport_m",
    user_id: "demo_user_carter",
    title: "Michael Carter - US Passport",
    category: "ids_travel",
    category_tab: "ids_travel",
    document_type: "Passport",
    description: "US Passport Book - Michael James Carter",
    passport_number: "563847291",
    issue_date: "2022-08-15",
    expiry_date: "2032-08-14",
    issuing_authority: "US Department of State",
    tags: ["passport", "travel", "id", "important"],
    file_url: null,
    notes: "Stored in fireproof safe. TSA PreCheck # 98374651."
  },
  {
    id: "demo_doc_passport_e",
    user_id: "demo_user_carter",
    title: "Emily Carter - US Passport",
    category: "ids_travel",
    category_tab: "ids_travel",
    document_type: "Passport",
    description: "US Passport Book - Emily Anne Carter",
    passport_number: "483726159",
    issue_date: "2022-08-15",
    expiry_date: "2032-08-14",
    issuing_authority: "US Department of State",
    tags: ["passport", "travel", "id"],
    file_url: null
  },
  {
    id: "demo_doc_passport_emma",
    user_id: "demo_user_carter",
    title: "Emma Carter - US Passport",
    category: "ids_travel",
    category_tab: "ids_travel",
    document_type: "Passport",
    description: "US Passport Book - Emma Grace Carter (Minor)",
    passport_number: "629384715",
    issue_date: "2023-02-10",
    expiry_date: "2028-02-09",
    issuing_authority: "US Department of State",
    tags: ["passport", "travel", "id", "child"],
    file_url: null,
    notes: "Minor passport - valid 5 years. Renew by 2028."
  },
  {
    id: "demo_doc_passport_jake",
    user_id: "demo_user_carter",
    title: "Jake Carter - US Passport",
    category: "ids_travel",
    category_tab: "ids_travel",
    document_type: "Passport",
    description: "US Passport Book - Jacob Michael Carter (Minor)",
    passport_number: "718394625",
    issue_date: "2023-02-10",
    expiry_date: "2028-02-09",
    issuing_authority: "US Department of State",
    tags: ["passport", "travel", "id", "child"],
    file_url: null
  },
  {
    id: "demo_doc_dl_m",
    user_id: "demo_user_carter",
    title: "Michael Carter - California Driver's License",
    category: "ids_travel",
    category_tab: "ids_travel",
    document_type: "Driver's License",
    description: "California Driver's License - Class C",
    license_number: "D1234567",
    issue_date: "2023-06-15",
    expiry_date: "2027-06-15",
    issuing_authority: "California DMV",
    address: "742 Maple Street, Oakville, CA 94102",
    tags: ["id", "driving", "legal"],
    file_url: null,
    notes: "Real ID compliant. Organ donor marked."
  },
  {
    id: "demo_doc_dl_e",
    user_id: "demo_user_carter",
    title: "Emily Carter - California Driver's License",
    category: "ids_travel",
    category_tab: "ids_travel",
    document_type: "Driver's License",
    description: "California Driver's License - Class C",
    license_number: "D7654321",
    issue_date: "2022-09-22",
    expiry_date: "2026-09-22",
    issuing_authority: "California DMV",
    tags: ["id", "driving", "legal"],
    file_url: null
  },
  {
    id: "demo_doc_ssn_m",
    user_id: "demo_user_carter",
    title: "Michael Carter - Social Security Card",
    category: "ids_travel",
    category_tab: "ids_travel",
    document_type: "Social Security Card",
    description: "Social Security Card - ***-**-4521",
    ssn_last_four: "4521",
    tags: ["ssn", "id", "important", "legal"],
    file_url: null,
    notes: "Original stored in safe deposit box. Copy in home safe."
  },
  
  // ========== INSURANCE ==========
  {
    id: "demo_doc_health_ins",
    user_id: "demo_user_carter",
    title: "BlueCross BlueShield - Family Health Insurance",
    category: "insurance",
    category_tab: "insurance",
    document_type: "Health Insurance Card",
    description: "BlueCross BlueShield PPO - Family Plan",
    policy_number: "BCB-987654321",
    group_number: "TechCorp-5000",
    member_id: "XYN123456789",
    effective_date: "2024-01-01",
    expiry_date: getDateDaysFromNow(365),
    provider_phone: "1-800-555-BCBS",
    tags: ["insurance", "medical", "important"],
    file_url: null,
    notes: "Covers all 4 family members. $30 copay PCP, $50 specialist. Deductible: $1,500 individual / $3,000 family."
  },
  {
    id: "demo_doc_dental_ins",
    user_id: "demo_user_carter",
    title: "Delta Dental - Family Dental Insurance",
    category: "insurance",
    category_tab: "insurance",
    document_type: "Dental Insurance Card",
    description: "Delta Dental PPO - Family Coverage",
    policy_number: "DD-456789123",
    group_number: "TechCorp-DEN",
    member_id: "DDN987654",
    expiry_date: getDateDaysFromNow(365),
    tags: ["insurance", "dental"],
    file_url: null,
    notes: "Annual max: $2,000/person. Orthodontia coverage for Emma's braces."
  },
  {
    id: "demo_doc_vision_ins",
    user_id: "demo_user_carter",
    title: "VSP Vision Care - Family Plan",
    category: "insurance",
    category_tab: "insurance",
    document_type: "Vision Insurance Card",
    description: "VSP Choice Plan - Family",
    policy_number: "VSP-789123456",
    member_id: "VSP456789",
    expiry_date: getDateDaysFromNow(365),
    tags: ["insurance", "vision"],
    file_url: null,
    notes: "Annual exam covered. $150 frame allowance. Michael needs new glasses in March."
  },
  {
    id: "demo_doc_home_ins",
    user_id: "demo_user_carter",
    title: "State Farm - Homeowners Insurance Policy",
    category: "insurance",
    category_tab: "insurance",
    document_type: "Homeowners Insurance",
    description: "HO-3 Policy - 742 Maple Street, Oakville",
    policy_number: "HO-789456123",
    effective_date: "2024-06-01",
    expiry_date: getDateDaysFromNow(300),
    premium_amount: 2400,
    coverage_amount: 450000,
    deductible: 2500,
    agent_name: "Tom Bradley",
    agent_phone: "(555) 222-3456",
    tags: ["insurance", "home", "important"],
    file_url: null,
    notes: "Replacement cost coverage. Earthquake rider added. Umbrella policy: $1M."
  },
  {
    id: "demo_doc_auto_ins",
    user_id: "demo_user_carter",
    title: "State Farm - Auto Insurance Policy",
    category: "insurance",
    category_tab: "insurance",
    document_type: "Auto Insurance",
    description: "Full Coverage - Both Vehicles",
    policy_number: "SF-AUTO-789456123",
    effective_date: "2024-03-01",
    expiry_date: getDateDaysFromNow(180),
    premium_amount: 2800,
    vehicles_covered: "2022 Toyota Highlander, 2021 Honda CR-V",
    agent_name: "Tom Bradley",
    agent_phone: "(555) 222-3456",
    tags: ["insurance", "auto", "important"],
    file_url: null,
    notes: "Good driver discount. Both vehicles on same policy. Roadside assistance included."
  },
  {
    id: "demo_doc_life_ins",
    user_id: "demo_user_carter",
    title: "Northwestern Mutual - Term Life Insurance",
    category: "insurance",
    category_tab: "insurance",
    document_type: "Life Insurance Policy",
    description: "Term Life - Michael Carter - $500,000",
    policy_number: "NW-LI-456789123",
    effective_date: "2020-01-15",
    expiry_date: "2040-01-14",
    premium_amount: 65,
    coverage_amount: 500000,
    beneficiary: "Emily Carter (100%)",
    tags: ["insurance", "life", "important"],
    file_url: null,
    notes: "20-year term policy. Premium locked in. Emily has matching $500K policy."
  },

  // ========== CAR / VEHICLE ==========
  {
    id: "demo_doc_reg_highlander",
    user_id: "demo_user_carter",
    title: "2022 Toyota Highlander - Registration",
    category: "car_registration",
    category_tab: "car_registration",
    document_type: "Vehicle Registration",
    description: "2022 Toyota Highlander XLE - Plate ABC-1234",
    vin: "5TDZA23C52S123456",
    license_plate: "ABC-1234",
    registered_owner: "Michael J. Carter",
    expiry_date: getDateDaysFromNow(180),
    tags: ["car", "registration", "legal"],
    file_url: null,
    notes: "Renewal online at DMV.ca.gov. Fee approx $275."
  },
  {
    id: "demo_doc_reg_crv",
    user_id: "demo_user_carter",
    title: "2021 Honda CR-V - Registration",
    category: "car_registration",
    category_tab: "car_registration",
    document_type: "Vehicle Registration",
    description: "2021 Honda CR-V EX-L - Plate XYZ-5678",
    vin: "2HKRW2H53MH654321",
    license_plate: "XYZ-5678",
    registered_owner: "Emily A. Carter",
    expiry_date: getDateDaysFromNow(220),
    tags: ["car", "registration", "legal"],
    file_url: null
  },
  {
    id: "demo_doc_title_highlander",
    user_id: "demo_user_carter",
    title: "Toyota Highlander - Vehicle Title",
    category: "car_registration",
    category_tab: "car_registration",
    document_type: "Vehicle Title",
    description: "Certificate of Title - 2022 Toyota Highlander",
    vin: "5TDZA23C52S123456",
    title_number: "CA123456789",
    issue_date: "2022-01-15",
    lienholder: "None - Paid Off",
    tags: ["car", "title", "legal", "important"],
    file_url: null,
    notes: "Title in safe deposit box. Purchased outright."
  },
  {
    id: "demo_doc_auto_ins_card",
    user_id: "demo_user_carter",
    title: "State Farm - Auto Insurance Card",
    category: "car_registration",
    category_tab: "car_registration",
    document_type: "Insurance Card",
    description: "Proof of Insurance - Both Vehicles",
    policy_number: "SF-AUTO-789456123",
    effective_date: "2024-03-01",
    expiry_date: getDateDaysFromNow(180),
    tags: ["car", "insurance"],
    file_url: null,
    notes: "Keep copies in both glove compartments."
  },

  // ========== LICENSES & CERTIFICATIONS ==========
  {
    id: "demo_doc_nursing_license",
    user_id: "demo_user_carter",
    title: "Emily Carter - RN License (Inactive)",
    category: "licenses_certs",
    category_tab: "licenses_certs",
    document_type: "Professional License",
    description: "RN License - Pre-librarian career",
    license_number: "RN-CA-789456",
    issue_date: "2005-05-15",
    expiry_date: "2020-05-15",
    issuing_authority: "California Board of Registered Nursing",
    status: "Inactive",
    tags: ["license", "professional"],
    file_url: null,
    notes: "Emily was an RN before transitioning to library science. Can reactivate with CEUs if needed."
  },
  {
    id: "demo_doc_cpr_cert",
    user_id: "demo_user_carter",
    title: "Michael Carter - CPR/First Aid Certification",
    category: "licenses_certs",
    category_tab: "licenses_certs",
    document_type: "Certification",
    description: "American Red Cross CPR/AED/First Aid",
    certificate_number: "RC-2024-456789",
    issue_date: getPastDate(180),
    expiry_date: getDateDaysFromNow(540),
    issuing_authority: "American Red Cross",
    tags: ["certification", "safety", "first aid"],
    file_url: null,
    notes: "Renewed for Jake's soccer coaching. Good for 2 years."
  },
  {
    id: "demo_doc_notary",
    user_id: "demo_user_carter",
    title: "Emily Carter - Notary Public Commission",
    category: "licenses_certs",
    category_tab: "licenses_certs",
    document_type: "Notary Commission",
    description: "State of California Notary Public",
    commission_number: "NP-2345678",
    issue_date: "2023-01-10",
    expiry_date: "2027-01-09",
    issuing_authority: "California Secretary of State",
    tags: ["license", "notary"],
    file_url: null,
    notes: "Emily got notary commission for library work. Bond and stamp in office desk."
  },

  // ========== HOME & UTILITIES ==========
  {
    id: "demo_doc_deed",
    user_id: "demo_user_carter",
    title: "Property Deed - 742 Maple Street",
    category: "home",
    category_tab: "home_utilities",
    document_type: "Property Deed",
    description: "Grant Deed - Carter Family Residence",
    property_address: "742 Maple Street, Oakville, CA 94102",
    parcel_number: "123-456-789",
    recorded_date: "2015-06-01",
    book_page: "Book 2015 / Page 456789",
    tags: ["property", "legal", "important"],
    file_url: null,
    notes: "Original recorded with Alameda County. Copy in safe deposit box."
  },
  {
    id: "demo_doc_mortgage",
    user_id: "demo_user_carter",
    title: "First National Bank - Mortgage Statement",
    category: "home",
    category_tab: "home_utilities",
    document_type: "Mortgage Statement",
    description: "30-Year Fixed Mortgage - 3.25% APR",
    account_number: "FNB-MTG-789456",
    lender: "First National Bank",
    original_amount: 380000,
    current_balance: 285000,
    monthly_payment: 1852.50,
    payoff_date: "2045-06-01",
    tags: ["mortgage", "financial", "important"],
    file_url: null,
    notes: "Auto-pay set up from Chase checking. Extra principal payments when possible."
  },
  {
    id: "demo_doc_warranty_roof",
    user_id: "demo_user_carter",
    title: "GAF Roof Warranty - 25 Year Shingles",
    category: "home",
    category_tab: "home_utilities",
    document_type: "Home Warranty",
    description: "GAF Timberline HDZ Shingles - Installed 2021",
    warranty_number: "GAF-2021-456789",
    issue_date: "2021-04-15",
    expiry_date: "2046-04-14",
    coverage: "25-year limited warranty on materials",
    contractor: "Oakville Roofing Co.",
    contractor_phone: "(555) ROOF-PRO",
    tags: ["warranty", "home", "roof"],
    file_url: null,
    notes: "Transferable to new owner if we sell. Keep receipt with warranty."
  },
  {
    id: "demo_doc_hoa_rules",
    user_id: "demo_user_carter",
    title: "Maple Grove HOA - Rules & Bylaws",
    category: "home",
    category_tab: "home_utilities",
    document_type: "HOA Documents",
    description: "CC&Rs, Bylaws, and Architectural Guidelines",
    hoa_name: "Maple Grove Homeowners Association",
    dues: "$75/month",
    tags: ["hoa", "home", "rules"],
    file_url: null,
    notes: "Annual meeting every March. Board approval needed for exterior changes."
  },

  // ========== WORK & SCHOOL ==========
  {
    id: "demo_doc_employment",
    user_id: "demo_user_carter",
    title: "TechCorp - Employment Agreement",
    category: "work",
    category_tab: "work_school",
    document_type: "Employment Contract",
    description: "Senior Software Engineer - Michael Carter",
    employer: "TechCorp Inc.",
    start_date: "2018-03-15",
    salary: "Confidential",
    benefits: "401k match, health/dental/vision, 4 weeks PTO",
    tags: ["employment", "work", "important"],
    file_url: null,
    notes: "Non-compete clause: 1 year in same industry within 50 miles."
  },
  {
    id: "demo_doc_401k",
    user_id: "demo_user_carter",
    title: "TechCorp 401(k) - Fidelity Summary",
    category: "work",
    category_tab: "work_school",
    document_type: "Benefits Summary",
    description: "Fidelity 401(k) Retirement Account",
    account_number: "FID-***-4521",
    employer_match: "6% match on 6% contribution",
    vesting: "Fully vested after 4 years",
    tags: ["retirement", "benefits", "financial"],
    file_url: null,
    notes: "Currently contributing 10%. Fidelity NetBenefits login: michael.carter@techcorp.com"
  },
  {
    id: "demo_doc_emma_504",
    user_id: "demo_user_carter",
    title: "Emma Carter - 504 Accommodation Plan",
    category: "education",
    category_tab: "work_school",
    document_type: "School Document",
    description: "Peanut Allergy Accommodation - Oakville High",
    school: "Oakville High School",
    school_year: "2024-2025",
    accommodations: "EpiPen storage, nut-free lunch table, nurse notification",
    tags: ["school", "medical", "child", "important"],
    file_url: null,
    notes: "Annual review meeting in August. Nurse: ext. 150. Contact: Ms. Williams, counselor."
  },
  {
    id: "demo_doc_jake_sports",
    user_id: "demo_user_carter",
    title: "Jake Carter - Sports Physical Clearance",
    category: "education",
    category_tab: "work_school",
    document_type: "School Form",
    description: "Annual Sports Physical Clearance",
    school: "Lincoln Elementary School",
    doctor: "Dr. Sarah Chen",
    clearance_date: getPastDate(60),
    expiry_date: getDateDaysFromNow(300),
    sports: "Soccer, Basketball, Baseball",
    tags: ["sports", "medical", "child", "school"],
    file_url: null,
    notes: "Cleared for all activities. No restrictions."
  },

  // ========== OTHER ==========
  {
    id: "demo_doc_marriage",
    user_id: "demo_user_carter",
    title: "Marriage Certificate - Michael & Emily Carter",
    category: "legal",
    category_tab: "other",
    document_type: "Marriage Certificate",
    description: "Certificate of Marriage - August 18, 2008",
    certificate_number: "MC-2008-789456",
    marriage_date: "2008-08-18",
    location: "St. Mary's Church, Oakville, CA",
    issuing_authority: "Alameda County Clerk",
    tags: ["legal", "important", "marriage"],
    file_url: null,
    notes: "Original in safe deposit box. Certified copy in home safe."
  },
  {
    id: "demo_doc_emma_birth",
    user_id: "demo_user_carter",
    title: "Emma Carter - Birth Certificate",
    category: "legal",
    category_tab: "other",
    document_type: "Birth Certificate",
    description: "Certificate of Live Birth - Emma Grace Carter",
    certificate_number: "BC-2011-123456",
    birth_date: "2011-03-08",
    birth_place: "Oakville Medical Center, Oakville, CA",
    tags: ["legal", "id", "child", "important"],
    file_url: null
  },
  {
    id: "demo_doc_jake_birth",
    user_id: "demo_user_carter",
    title: "Jake Carter - Birth Certificate",
    category: "legal",
    category_tab: "other",
    document_type: "Birth Certificate",
    description: "Certificate of Live Birth - Jacob Michael Carter",
    certificate_number: "BC-2013-654321",
    birth_date: "2013-11-20",
    birth_place: "Oakville Medical Center, Oakville, CA",
    tags: ["legal", "id", "child", "important"],
    file_url: null
  },
  {
    id: "demo_doc_wills",
    user_id: "demo_user_carter",
    title: "Last Will & Testament - Carter Family",
    category: "legal",
    category_tab: "other",
    document_type: "Legal Document",
    description: "Wills and Living Trust Documents - Updated 2023",
    attorney: "Jennifer Walsh, Esq.",
    attorney_phone: "(555) LAW-HELP",
    issue_date: "2023-01-15",
    tags: ["legal", "estate", "important"],
    file_url: null,
    notes: "Originals at attorney's office. Copies in safe deposit box. Guardian designated: David Reynolds (Emily's brother)."
  },
  {
    id: "demo_doc_buddy_records",
    user_id: "demo_user_carter",
    title: "Buddy - Vaccination & Health Records",
    category: "pet",
    category_tab: "other",
    document_type: "Pet Records",
    description: "Golden Retriever - Vaccination & Health Records",
    pet_name: "Buddy",
    microchip: "985112345678901",
    rabies_cert: "RAB-2024-7890",
    rabies_expiry: getDateDaysFromNow(365),
    vet: "Dr. Jennifer Woods - Oakville Animal Hospital",
    vet_phone: "(555) 789-0123",
    tags: ["pet", "medical", "vet"],
    file_url: null,
    notes: "DHPP, Bordetella current. Rabies due next December. Microchip registered with HomeAgain."
  },
  {
    id: "demo_doc_warranty_tv",
    user_id: "demo_user_carter",
    title: "Samsung 65\" TV - Extended Warranty",
    category: "warranty",
    category_tab: "other",
    document_type: "Product Warranty",
    description: "65\" Samsung QLED TV - 5 Year Protection Plan",
    serial_number: "SN-TV-456789123",
    purchase_date: "2023-11-24",
    warranty_expiry: "2028-11-24",
    retailer: "Best Buy",
    plan_number: "BBY-PROTECT-789",
    tags: ["warranty", "electronics"],
    file_url: null,
    notes: "Covers accidental damage. Best Buy Geek Squad for service."
  }
];

// ============= HOUSEHOLD INFO =============

export const DEMO_HOUSEHOLD = {
  id: "demo_household_001",
  user_id: "demo_user_carter",
  address: "742 Maple Street, Oakville, ST 12345",
  home_type: "Single Family Home",
  year_built: 1998,
  square_feet: 2400,
  bedrooms: 4,
  bathrooms: 2.5,
  
  // WiFi & Tech
  wifi_network: "CarterFamily_5G",
  wifi_password: "BuddyLoves2024!",
  router_location: "Office closet, top shelf",
  smart_home: "Google Home, Nest Thermostat, Ring doorbell",
  
  // Security
  alarm_code: "1234",
  alarm_company: "SafeHome Security",
  alarm_phone: "(555) 999-SAFE",
  alarm_account: "SH-742MAPLE",
  garage_code: "5678",
  
  // Access
  spare_key_location: "Under fake rock by back door (left of steps)",
  neighbor_with_key: "Susan Miller at 744 Maple - (555) 222-3333",
  locksmith: "Keys & Locks Plus - (555) 555-KEYS",
  
  // Utilities
  water_shutoff: "Basement, behind furnace, red valve on left pipe",
  gas_shutoff: "Side of house (north), yellow valve near meter",
  electrical_panel: "Garage, left wall. Main breaker at top.",
  
  // Services
  trash_day: "Wednesday (put out Tuesday night)",
  recycling_day: "Wednesday (same as trash)",
  yard_waste_day: "First Wednesday of month",
  mail_delivery: "Usually between 2-4pm",
  
  // HOA
  hoa_name: "Maple Grove HOA",
  hoa_contact: "hoa@maplegrove.org",
  hoa_phone: "(555) HOA-1234",
  hoa_dues: "$75/month, due 1st of month",
  
  // Emergency
  fire_extinguisher_locations: "Kitchen (under sink), Garage (by door), Basement (foot of stairs)",
  first_aid_kit: "Upstairs bathroom, under sink",
  flashlights: "Kitchen junk drawer, nightstands, garage workbench",
  
  notes: "Sump pump in basement - check monthly. Water heater (2021) in basement, set to 120°F. Furnace filter: 20x25x1, change every 3 months."
};

// ============= SHOPPING LISTS =============

export const DEMO_SHOPPING_LISTS = [
  {
    id: "demo_list_001",
    user_id: "demo_user_carter",
    name: "Weekly Groceries",
    store: "Costco",
    items: [
      { id: "item_001", name: "Milk (2 gallons)", checked: false },
      { id: "item_002", name: "Bread (2 loaves)", checked: true },
      { id: "item_003", name: "Eggs (36 ct)", checked: false },
      { id: "item_004", name: "Chicken breast (family pack)", checked: false },
      { id: "item_005", name: "Bananas", checked: true },
      { id: "item_006", name: "Buddy's dog food (Blue Buffalo 30lb)", checked: false },
      { id: "item_007", name: "Kids' lunch snacks", checked: false },
      { id: "item_008", name: "Paper towels", checked: false },
      { id: "item_009", name: "Laundry detergent", checked: false }
    ],
    created_at: getPastDate(2)
  },
  {
    id: "demo_list_002",
    user_id: "demo_user_carter",
    name: "Emma's School Supplies",
    store: "Target",
    items: [
      { id: "item_010", name: "3-ring binders (2)", checked: true },
      { id: "item_011", name: "College ruled notebooks (5)", checked: true },
      { id: "item_012", name: "Mechanical pencils", checked: false },
      { id: "item_013", name: "Highlighters (assorted)", checked: false },
      { id: "item_014", name: "Scientific calculator", checked: false }
    ],
    created_at: getPastDate(5)
  },
  {
    id: "demo_list_003",
    user_id: "demo_user_carter",
    name: "Lake Tahoe Trip",
    store: "Various",
    items: [
      { id: "item_015", name: "Sunscreen SPF 50", checked: false },
      { id: "item_016", name: "Bug spray", checked: false },
      { id: "item_017", name: "Hiking snacks", checked: false },
      { id: "item_018", name: "Buddy's travel food container", checked: false },
      { id: "item_019", name: "First aid kit refills", checked: false },
      { id: "item_020", name: "Camera memory card", checked: false }
    ],
    created_at: getPastDate(1)
  }
];

// ============= FINANCIAL OVERVIEW (For Emergency Binder) =============

export const DEMO_FINANCIAL = {
  id: "demo_financial_001",
  user_id: "demo_user_carter",
  
  bank_accounts: [
    {
      institution: "First National Bank",
      account_type: "Joint Checking",
      account_nickname: "Main Checking",
      last_four: "4567",
      routing_number: "123456789",
      online_banking: "firstnational.com/login",
      notes: "Primary account for bills, direct deposit, and daily expenses. Auto-pay for utilities and mortgage."
    },
    {
      institution: "First National Bank",
      account_type: "Joint Savings",
      account_nickname: "Emergency Fund",
      last_four: "8901",
      notes: "6 months expenses saved. Don't touch except for true emergencies."
    },
    {
      institution: "First National Bank",
      account_type: "Savings",
      account_nickname: "Vacation Fund",
      last_four: "2345",
      notes: "Auto-transfer $200/month from checking. Goal: $5000 for Lake Tahoe and Disney next year."
    },
    {
      institution: "Ally Bank",
      account_type: "High-Yield Savings",
      account_nickname: "Kids' College Fund",
      last_four: "6789",
      notes: "In addition to 529 plans. Currently $15,000. Add birthday/holiday money gifts."
    }
  ],
  
  credit_cards: [
    {
      issuer: "Chase",
      card_name: "Sapphire Preferred",
      last_four: "1234",
      primary_holder: "Michael Carter",
      credit_limit: "$15,000",
      rewards: "Travel points - 2x on dining & travel",
      payment_due: "15th of month",
      auto_pay: true,
      notes: "Primary card for travel and dining. Pay in full monthly."
    },
    {
      issuer: "American Express",
      card_name: "Blue Cash Everyday",
      last_four: "5678",
      primary_holder: "Emily Carter",
      credit_limit: "$10,000",
      rewards: "3% groceries, 2% gas",
      payment_due: "22nd of month",
      auto_pay: true,
      notes: "Groceries and gas only. Pay in full monthly."
    },
    {
      issuer: "Costco/Citi",
      card_name: "Costco Anywhere Visa",
      last_four: "9012",
      primary_holder: "Michael Carter",
      credit_limit: "$12,000",
      rewards: "4% gas, 3% restaurants, 2% Costco",
      payment_due: "8th of month",
      auto_pay: true,
      notes: "Use for Costco and gas. Annual rebate in February."
    }
  ],
  
  investments: [
    {
      institution: "Fidelity",
      account_type: "401(k)",
      account_nickname: "Michael's Retirement",
      holder: "Michael Carter",
      beneficiary: "Emily Carter (100%)",
      notes: "Through TechCorp. 6% contribution + 4% employer match. Target date 2045 fund."
    },
    {
      institution: "Vanguard",
      account_type: "Roth IRA",
      account_nickname: "Emily's Retirement",
      holder: "Emily Carter",
      beneficiary: "Michael Carter (100%)",
      notes: "Max contribution $7,000/year. Invested in index funds (VTSAX)."
    },
    {
      institution: "Vanguard",
      account_type: "529 College Savings",
      account_nickname: "Emma's 529",
      holder: "Michael Carter",
      beneficiary: "Emma Carter",
      notes: "State tax deduction. Auto-invest $300/month. Currently ~$45,000."
    },
    {
      institution: "Vanguard",
      account_type: "529 College Savings",
      account_nickname: "Jake's 529",
      holder: "Michael Carter",
      beneficiary: "Jake Carter",
      notes: "State tax deduction. Auto-invest $300/month. Currently ~$32,000."
    }
  ],
  
  loans: [
    {
      type: "Mortgage",
      lender: "First National Bank",
      property: "742 Maple Street",
      original_amount: "$320,000",
      current_balance: "~$245,000",
      interest_rate: "3.25%",
      monthly_payment: "$1,892",
      payment_due: "1st of month",
      auto_pay: true,
      maturity_date: "2045",
      notes: "30-year fixed. Refinanced in 2021 from 4.5%. Escrow includes taxes and insurance."
    }
  ],
  
  important_contacts: {
    financial_advisor: "Tom Williams, First National Bank - (555) 111-BANK",
    accountant: "Sarah Chen, CPA - (555) 222-TAXS",
    estate_attorney: "Jennifer Adams, Adams & Associates - (555) LAW-1234"
  },
  
  notes: "Safe deposit box at First National Bank - Box #247. Contains: original wills, deed, car titles, birth certificates, passports. Key in fireproof home safe (combo: 36-24-36)."
};

// ============= END OF LIFE PLANNING =============

export const DEMO_END_OF_LIFE = {
  id: "demo_eol_001",
  user_id: "demo_user_carter",
  
  will_and_trust: {
    will_location: "Safe deposit box #247 at First National Bank + copy in home safe",
    will_date: "2023-01-15",
    will_attorney: "Jennifer Adams",
    attorney_firm: "Adams & Associates Law",
    attorney_phone: "(555) LAW-1234",
    attorney_address: "100 Legal Way, Suite 500, Oakville, ST 12345",
    executor: "Emily Carter (spouse), Alternate: David Reynolds (brother-in-law)",
    guardian_for_minors: "David Reynolds & Lisa Reynolds, Alternate: Margaret Carter"
  },
  
  life_insurance: [
    {
      provider: "MetLife",
      policy_type: "Group Term Life",
      policy_number: "ML-TECHCORP-789456",
      coverage_amount: "$240,000",
      holder: "Michael Carter",
      beneficiary: "Emily Carter (100%)",
      contingent_beneficiary: "Emma Carter (50%), Jake Carter (50%)",
      through_employer: true,
      notes: "Through TechCorp - 2x annual salary. Terminates if employment ends."
    },
    {
      provider: "Northwestern Mutual",
      policy_type: "Term Life (20-year)",
      policy_number: "NM-456789123",
      coverage_amount: "$500,000",
      holder: "Michael Carter",
      beneficiary: "Emily Carter (100%)",
      contingent_beneficiary: "Emma Carter (50%), Jake Carter (50%)",
      premium: "$65/month",
      term_ends: "2038",
      notes: "Additional coverage purchased in 2018. Covers mortgage and kids' college."
    },
    {
      provider: "Northwestern Mutual",
      policy_type: "Term Life (20-year)",
      policy_number: "NM-456789124",
      coverage_amount: "$250,000",
      holder: "Emily Carter",
      beneficiary: "Michael Carter (100%)",
      contingent_beneficiary: "Emma Carter (50%), Jake Carter (50%)",
      premium: "$45/month",
      term_ends: "2038",
      notes: "Covers Emily. Matched to income replacement needs."
    }
  ],
  
  power_of_attorney: {
    financial_poa: "Emily Carter (spouse)",
    financial_poa_alternate: "David Reynolds",
    healthcare_proxy: "Emily Carter (spouse)",
    healthcare_proxy_alternate: "Margaret Carter",
    document_location: "Safe deposit box and attorney's office"
  },
  
  healthcare_directives: {
    living_will: true,
    living_will_location: "Safe deposit box, copy with Dr. Robert Chen",
    dnr_status: "No (full code)",
    organ_donor: {
      michael: true,
      emily: true
    },
    tissue_donor: {
      michael: true,
      emily: true
    },
    wishes: "Both prefer quality of life over prolonging life. No extraordinary measures if prognosis is terminal with no reasonable chance of recovery. Discuss specific scenarios with healthcare proxy."
  },
  
  final_wishes: {
    michael: {
      burial_preference: "Cremation",
      service_preference: "Celebration of life - casual gathering with family and friends",
      memorial_donations: "American Heart Association or local food bank",
      special_instructions: "Scatter ashes at Lake Tahoe (family vacation spot). Play 'What a Wonderful World' at service."
    },
    emily: {
      burial_preference: "Cremation",
      service_preference: "Small family service, then celebration of life",
      memorial_donations: "Local library foundation or animal shelter",
      special_instructions: "Plant a tree in the backyard. Keep it simple."
    }
  },
  
  digital_assets: {
    password_manager: "1Password - master password in sealed envelope in home safe",
    important_accounts: "Google (photos, docs), Facebook, Apple ID - credentials in password manager",
    social_media_wishes: "Memorialize accounts, don't delete - kids may want access to photos",
    notes: "Digital legacy contact set up on Apple ID (Emily) and Google (Emily)"
  },
  
  notes: "Both wills reviewed and updated January 2023 after Jake turned 10. Review again when Emma turns 18. All documents are in safe deposit box with copies in fireproof home safe and attorney's office."
};

// ============= FAMILY SHARING =============

export const DEMO_FAMILY_SHARING = {
  id: "demo_sharing_001",
  user_id: "demo_user_carter",
  owner: {
    name: "Michael Carter",
    email: "michael.carter@demo.forgetmenot.app",
    role: "owner"
  },
  members: [
    {
      id: "share_emily",
      name: "Emily Carter",
      email: "emily.carter@email.com",
      relationship: "Spouse",
      role: "admin",
      permissions: ["view_all", "edit_all", "add", "delete", "share"],
      invited_date: "2023-01-20",
      accepted_date: "2023-01-20",
      last_active: getPastDate(1),
      notes: "Full access to everything"
    },
    {
      id: "share_margaret",
      name: "Margaret Carter",
      email: "margaret.carter@email.com",
      relationship: "Grandmother",
      role: "viewer",
      permissions: ["view_emergency_contacts", "view_medical", "view_kids_info"],
      invited_date: "2023-02-15",
      accepted_date: "2023-02-16",
      last_active: getPastDate(7),
      notes: "Can see emergency info and kids' details for babysitting"
    }
  ]
};

// ============= CAREGIVER ACCESS =============

export const DEMO_CAREGIVER_ACCESS = {
  id: "demo_caregiver_001",
  user_id: "demo_user_carter",
  caregivers: [
    {
      id: "caregiver_001",
      name: "Susan Miller",
      relationship: "Neighbor / Emergency Contact",
      phone: "(555) 222-3333",
      email: "susan.miller@email.com",
      access_code: "CARTER-2024",
      access_level: "emergency",
      can_access: ["emergency_contacts", "medical_allergies", "medications", "household_entry"],
      restrictions: ["financial", "legal_documents", "end_of_life"],
      granted_date: "2023-06-01",
      last_accessed: getPastDate(30),
      expires: null,
      notes: "Next-door neighbor. Has house key. Access for kid emergencies or if we're traveling."
    },
    {
      id: "caregiver_002",
      name: "Lisa Thompson",
      relationship: "Babysitter",
      phone: "(555) 777-8888",
      email: "lisa.t@email.com",
      access_code: "SITTER-2024",
      access_level: "limited",
      can_access: ["emergency_contacts", "medical_allergies", "kids_medications", "household_wifi"],
      restrictions: ["financial", "legal_documents", "end_of_life", "adult_medical"],
      granted_date: "2024-01-15",
      last_accessed: getPastDate(14),
      expires: getDateDaysFromNow(180),
      notes: "Regular Friday night babysitter. High school senior, very responsible."
    }
  ]
};

// ============= HELPER: Get all demo data as a single object =============

export const getAllDemoData = () => ({
  user: DEMO_USER,
  profiles: [...DEMO_PROFILES],
  medicalProfile: { ...DEMO_MEDICAL_PROFILE },
  allergies: [...DEMO_ALLERGIES],
  medications: [...DEMO_MEDICATIONS],
  conditions: [...DEMO_CONDITIONS],
  surgeries: [...DEMO_SURGERIES],
  doctors: [...DEMO_DOCTORS],
  insurance: [...DEMO_INSURANCE],
  emergencyContacts: [...DEMO_EMERGENCY_CONTACTS],
  pets: [...DEMO_PETS],
  vehicles: [...DEMO_VEHICLES],
  maintenanceRecords: [...DEMO_MAINTENANCE_RECORDS],
  parkingLocations: [...DEMO_PARKING_LOCATIONS],
  importantDates: [...DEMO_IMPORTANT_DATES],
  contacts: [...DEMO_CONTACTS],
  documents: [...DEMO_DOCUMENTS],
  places: [...DEMO_PLACES],
  reimbursements: [...DEMO_REIMBURSEMENTS],
  household: { ...DEMO_HOUSEHOLD },
  shoppingLists: [...DEMO_SHOPPING_LISTS],
  financial: { ...DEMO_FINANCIAL },
  endOfLife: { ...DEMO_END_OF_LIFE },
  familySharing: { ...DEMO_FAMILY_SHARING },
  caregiverAccess: { ...DEMO_CAREGIVER_ACCESS }
});

// ============= HELPER: Generate new ID for demo items =============

export { generateId };

export default {
  DEMO_USER,
  DEMO_PROFILES,
  DEMO_MEDICAL_PROFILE,
  DEMO_ALLERGIES,
  DEMO_MEDICATIONS,
  DEMO_CONDITIONS,
  DEMO_SURGERIES,
  DEMO_DOCTORS,
  DEMO_INSURANCE,
  DEMO_EMERGENCY_CONTACTS,
  DEMO_PETS,
  DEMO_VEHICLES,
  DEMO_MAINTENANCE_RECORDS,
  DEMO_PARKING_LOCATIONS,
  DEMO_IMPORTANT_DATES,
  DEMO_CONTACTS,
  DEMO_PLACES,
  DEMO_REIMBURSEMENTS,
  DEMO_DOCUMENTS,
  DEMO_HOUSEHOLD,
  DEMO_SHOPPING_LISTS,
  DEMO_FINANCIAL,
  DEMO_END_OF_LIFE,
  DEMO_FAMILY_SHARING,
  DEMO_CAREGIVER_ACCESS,
  getAllDemoData,
  generateId
};
